// src/HomePage/Game/FolkGame/Caro/CaroGame.js
// REFACTORED: bỏ mutate prop room, dùng local state + refs, cleanup bot timeout,
// optimistic update cho online mode, roomRef cho timer (chống stale closure).
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import CaroBoard from './CaroBoard';
import { createEmptyBoard, normalizeBoard, checkCaroWinner, getBotCaroMove } from './CaroLogic';
import { updateFolkGameState, toggleRematchReady, surrenderFolkGame } from '../FolkGameService';
import './CaroGame.css';

const CARO_TIME_LIMIT = 30;
const CARO_GRACE_SECONDS = 2;
const CARO_BOT_DELAY_MS = 450;
const BOARD_SIZE = 15;

const CaroGame = ({ room: roomProp, currentUser, isBotMode, onLeave }) => {
  // ============== STATE đồng bộ từ prop room.gameState ==============
  const [board, setBoard] = useState(() => normalizeBoard(roomProp.gameState?.board));
  const [lastMove, setLastMove] = useState(roomProp.gameState?.lastMove || null);
  const [winningLine, setWinningLine] = useState(roomProp.gameState?.winningLine || []);

  // ============== STATE riêng cho bot mode (THAY THẾ việc mutate prop) ==============
  // Ở bot mode, prop room không bao giờ update từ Firebase, nên ta lưu các trường
  // cần thay đổi cục bộ vào đây. Khi online mode thì giữ null, dùng prop trực tiếp.
  const [botOverrides, setBotOverrides] = useState(() => isBotMode ? {
    status: roomProp.status,
    currentTurn: roomProp.currentTurn,
    winner: roomProp.winner,
    endReason: roomProp.endReason || '',
    lastMoveAt: roomProp.lastMoveAt || Date.now()
  } : null);

  // ============== State UI phụ ==============
  const [timeLeft, setTimeLeft] = useState(CARO_TIME_LIMIT);
  const [botScore, setBotScore] = useState({ p1: 0, p2: 0 });
  const [botStarter, setBotStarter] = useState(() => {
    try {
      return sessionStorage.getItem('caro_bot_starter') || 'player';
    } catch {
      return 'player';
    }
  });

  // ============== REFS ==============
  const timerRef = useRef(null);
  const botTimeoutRef = useRef(null);
  const roomRef = useRef(roomProp);
  const overridesRef = useRef(botOverrides);
  const defeatProcessedRef = useRef(false);
  const moveInFlightRef = useRef(false);
  const [isMoveInFlight, setIsMoveInFlight] = useState(false);

  // ============== DERIVED: room thật dùng để render ==============
  const room = useMemo(() => {
    if (isBotMode && botOverrides) {
      return { ...roomProp, ...botOverrides };
    }
    return roomProp;
  }, [roomProp, botOverrides, isBotMode]);

  const isHost = room.player1?.id === currentUser?.id;
  const mySymbol = isHost ? 'X' : 'O';
  const isMyTurn = room.status === 'playing' && room.currentTurn === currentUser?.id;
  const currentDifficulty = room.botDifficulty || 'hard';

  // ============== Giữ ref luôn trỏ tới giá trị mới nhất ==============
  useEffect(() => { roomRef.current = room; }, [room]);
  useEffect(() => { overridesRef.current = botOverrides; }, [botOverrides]);
  useEffect(() => {
    try { sessionStorage.setItem('caro_bot_starter', botStarter); } catch {}
  }, [botStarter]);

  // ============== Helpers ==============
  const getPlayerFullName = (player) => {
    if (!player) return '';
    const holy = player.holyName ? `${player.holyName} ` : '';
    const full = player.fullName || player.name || 'Người chơi';
    return `${holy}${full}`.trim();
  };

  const findFirstEmptyCell = (b) => {
    for (let i = 0; i < BOARD_SIZE; i++) {
      for (let j = 0; j < BOARD_SIZE; j++) {
        if (!b[i][j]) return { r: i, c: j };
      }
    }
    return null;
  };

  const clearBotTimeout = () => {
    if (botTimeoutRef.current) {
      clearTimeout(botTimeoutRef.current);
      botTimeoutRef.current = null;
    }
  };

  // ============== Đồng bộ board từ prop khi online ==============
  useEffect(() => {
    if (isBotMode) return;
    if (roomProp.gameState && roomProp.gameState.board) {
      setBoard(normalizeBoard(roomProp.gameState.board));
      setLastMove(roomProp.gameState.lastMove || null);
      setWinningLine(roomProp.gameState.winningLine || []);
    } else if (roomProp.status === 'playing') {
      setBoard(createEmptyBoard());
      setLastMove(null);
      setWinningLine([]);
    }
  }, [roomProp.gameState, roomProp.status, isBotMode]);

  // ============== Cleanup khi unmount ==============
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      clearBotTimeout();
    };
  }, []);

  // ============== Xử thua do hết giờ ==============
  const handleTimeoutDefeat = useCallback(async () => {
    if (defeatProcessedRef.current) return;
    defeatProcessedRef.current = true;
    clearInterval(timerRef.current);

    const currentRoom = roomRef.current;
    if (currentRoom.status !== 'playing') return;

    const timedOutPlayerId = currentRoom.currentTurn;
    const timedOutPlayer = currentRoom.player1?.id === timedOutPlayerId
      ? currentRoom.player1
      : currentRoom.player2;
    const opponent = currentRoom.player1?.id === timedOutPlayerId
      ? currentRoom.player2
      : currentRoom.player1;
    const opponentId = isBotMode
      ? (timedOutPlayerId === 'bot' ? currentUser?.id : 'bot')
      : opponent?.id;

    const timeoutReason = `⏱️ ${getPlayerFullName(timedOutPlayer || { fullName: 'Đối thủ' })} đã bị xử thua do hết thời gian!`;

    if (isBotMode) {
      setBotOverrides({
        status: 'finished',
        winner: opponentId,
        endReason: timeoutReason,
        currentTurn: currentRoom.currentTurn,
        lastMoveAt: Date.now()
      });
      if (opponentId === currentUser?.id) {
        setBotScore((prev) => ({ ...prev, p1: prev.p1 + 1 }));
      } else {
        setBotScore((prev) => ({ ...prev, p2: prev.p2 + 1 }));
      }
    } else {
      if (opponentId) {
        await updateFolkGameState(
          'caro',
          currentRoom.id,
          {
            board: normalizeBoard(currentRoom.gameState?.board) || createEmptyBoard(),
            lastMove: currentRoom.gameState?.lastMove || null,
            winningLine: []
          },
          timedOutPlayerId,
          opponentId,
          timeoutReason
        );
      }
    }
  }, [isBotMode, currentUser]);

  // ============== BỘ ĐẾM THỜI GIAN ==============
  useEffect(() => {
    if (room.status !== 'playing') {
      clearInterval(timerRef.current);
      return;
    }

    setTimeLeft(CARO_TIME_LIMIT);
    clearInterval(timerRef.current);
    defeatProcessedRef.current = false;

    let currentSeconds = CARO_TIME_LIMIT;

    timerRef.current = setInterval(async () => {
      currentSeconds -= 1;
      setTimeLeft(Math.max(0, currentSeconds));

      // Bù trễ mạng 2s
      if (currentSeconds <= -CARO_GRACE_SECONDS) {
        clearInterval(timerRef.current);
        await handleTimeoutDefeat();
      }
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [room.currentTurn, room.status, room.lastMoveAt, handleTimeoutDefeat]);

  // ============== Click ô cờ ==============
  const handleCellClick = useCallback(async (r, c) => {
    if (moveInFlightRef.current) return;

    const currentBoard = normalizeBoard(board);
    if (!isMyTurn || currentBoard[r][c] !== null || room.status !== 'playing') return;

    // Sửa #6: Cho phép click trong thời gian ân xá (timeLeft = 0..-2) nếu server chưa báo timeout
    // Nhưng nếu user click đúng lúc timeLeft=0, ta vẫn cho đánh - timeout sẽ tự xử nếu quá -2s

    moveInFlightRef.current = true;
    setIsMoveInFlight(true);

    // Hủy bất kỳ bot move nào đang chờ (phòng trường hợp edge case)
    clearBotTimeout();

    try {
      const newBoard = currentBoard.map((row) => [...row]);
      newBoard[r][c] = mySymbol;
      const moveInfo = { r, c };

      const winResult = checkCaroWinner(newBoard, r, c);
      const nextWinningLine = winResult ? winResult.winningLine : [];
      const winnerId = winResult
        ? (winResult.winner === 'DRAW' ? 'DRAW' : currentUser.id)
        : null;

      // OPTIMISTIC UPDATE: cập nhật UI ngay lập tức
      setBoard(newBoard);
      setLastMove(moveInfo);
      setWinningLine(nextWinningLine);

      if (isBotMode) {
        if (winResult) {
          setBotOverrides({
            status: 'finished',
            winner: winnerId,
            endReason: '',
            currentTurn: currentUser?.id,
            lastMoveAt: Date.now()
          });
          if (winnerId === currentUser.id) {
            setBotScore((prev) => ({ ...prev, p1: prev.p1 + 1 }));
          }
          return;
        }

        setBotOverrides((prev) => ({
          ...(prev || {}),
          currentTurn: 'bot',
          lastMoveAt: Date.now()
        }));

        // Lên lịch bot đánh, lưu ref để cleanup
        botTimeoutRef.current = setTimeout(() => {
          botTimeoutRef.current = null;

          // Kiểm tra game còn đang chơi không (surrender/timeout có thể đã set finished)
          if (overridesRef.current?.status !== 'playing') return;

          const aiMove = getBotCaroMove(newBoard, 'O', currentDifficulty);
          let botMove = aiMove;
          // Safety: nếu AI trả ô không hợp lệ thì fallback
          if (!botMove || newBoard[botMove.r][botMove.c] !== null) {
            botMove = findFirstEmptyCell(newBoard);
          }
          if (!botMove) return;

          const botBoard = newBoard.map((row) => [...row]);
          botBoard[botMove.r][botMove.c] = 'O';

          const botWin = checkCaroWinner(botBoard, botMove.r, botMove.c);
          setBoard(botBoard);
          setLastMove(botMove);
          setWinningLine(botWin ? botWin.winningLine : []);

          if (botWin) {
            setBotOverrides({
              status: 'finished',
              winner: botWin.winner === 'DRAW' ? 'DRAW' : 'bot',
              endReason: '',
              currentTurn: 'bot',
              lastMoveAt: Date.now()
            });
            if (botWin.winner !== 'DRAW') {
              setBotScore((prev) => ({ ...prev, p2: prev.p2 + 1 }));
            }
          } else {
            setBotOverrides((prev) => ({
              ...(prev || {}),
              currentTurn: currentUser.id,
              lastMoveAt: Date.now()
            }));
          }
        }, CARO_BOT_DELAY_MS);
      } else {
        // Online mode: optimistic đã set ở trên, giờ gửi Firebase
        const nextTurnId = isHost ? room.player2.id : room.player1.id;
        await updateFolkGameState(
          'caro',
          room.id,
          { board: newBoard, lastMove: moveInfo, winningLine: nextWinningLine },
          nextTurnId,
          winnerId
        );
      }
    } finally {
      moveInFlightRef.current = false;
      setIsMoveInFlight(false);
    }
  }, [board, isMyTurn, mySymbol, isHost, isBotMode, currentDifficulty, currentUser, room]);

  // ============== Chịu thua ==============
  const handleSurrender = useCallback(async () => {
    if (window.confirm('Bạn có chắc chắn muốn xin đầu hàng ván đấu này không?')) {
      clearBotTimeout();

      const opponentId = isBotMode
        ? 'bot'
        : (isHost ? room.player2?.id : room.player1?.id);
      const surrenderReason = `🏳️ ${getPlayerFullName(currentUser)} đã xin đầu hàng!`;

      if (isBotMode) {
        setBotOverrides({
          status: 'finished',
          winner: 'bot',
          endReason: surrenderReason,
          currentTurn: 'bot',
          lastMoveAt: Date.now()
        });
        setBotScore((prev) => ({ ...prev, p2: prev.p2 + 1 }));
      } else {
        await surrenderFolkGame('caro', room.id, opponentId, surrenderReason);
      }
    }
  }, [isBotMode, isHost, room, currentUser]);

  // ============== Rời phòng trong trận ==============
  const handleInGameLeave = useCallback(() => {
    const confirmMsg = isBotMode
      ? 'Bạn có chắc chắn muốn thoát khỏi ván luyện tập?'
      : 'Nếu rời phòng khi đang thi đấu, bạn sẽ bị xử thua. Bạn có chắc chắn muốn rời đi không?';

    if (window.confirm(confirmMsg)) {
      clearBotTimeout();
      onLeave();
    }
  }, [isBotMode, onLeave]);

  // ============== Đấu lại ==============
  const myRematchStatus = isHost ? room.player1?.rematch : room.player2?.rematch;
  const opponentRematchStatus = isHost ? room.player2?.rematch : room.player1?.rematch;

  const handleRematchClick = useCallback(async () => {
    if (isBotMode) {
      clearBotTimeout();

      const nextStarter = botStarter === 'player' ? 'bot' : 'player';
      setBotStarter(nextStarter);

      // Reset ván mới
      const newBoard = createEmptyBoard();
      setBoard(newBoard);
      setLastMove(null);
      setWinningLine([]);

      const baseOverrides = {
        status: 'playing',
        winner: null,
        endReason: '',
        lastMoveAt: Date.now()
      };

      if (nextStarter === 'bot') {
        setBotOverrides({ ...baseOverrides, currentTurn: 'bot' });
        setTimeLeft(CARO_TIME_LIMIT);

        botTimeoutRef.current = setTimeout(() => {
          botTimeoutRef.current = null;
          if (overridesRef.current?.status !== 'playing') return;

          const freshBoard = createEmptyBoard();
          freshBoard[7][7] = 'O';
          setBoard(freshBoard);
          setLastMove({ r: 7, c: 7 });
          setBotOverrides((prev) => ({
            ...(prev || {}),
            currentTurn: currentUser?.id || 'guest'
          }));
        }, CARO_BOT_DELAY_MS);
      } else {
        setBotOverrides({
          ...baseOverrides,
          currentTurn: currentUser?.id || 'guest'
        });
        setTimeLeft(CARO_TIME_LIMIT);
      }
    } else {
      await toggleRematchReady('caro', room.id, isHost, !myRematchStatus);
    }
  }, [isBotMode, botStarter, currentUser, room, isHost, myRematchStatus]);

  // ============== Derived UI ==============
  const scoreP1 = isBotMode ? botScore.p1 : (room.score?.p1 || 0);
  const scoreP2 = isBotMode ? botScore.p2 : (room.score?.p2 || 0);

  const isP1Turn = room.currentTurn === room.player1?.id && room.status === 'playing';
  const isP2Turn = room.currentTurn === room.player2?.id && room.status === 'playing';

  return (
    <div className="caro-game-container">
      <div className="caro-scoreboard-card">
        <div className="caro-players-row">
          <div className={`caro-player-box left ${isP1Turn ? 'active-turn' : ''}`}>
            <div className="caro-player-avatar">{room.player1?.avatar || '🎓'}</div>
            <div className="caro-player-meta">
              <strong className="caro-player-name">{getPlayerFullName(room.player1)}</strong>
            </div>
            <span className="caro-symbol x">✕</span>
          </div>

          <div className="caro-vs-text">VS</div>

          <div className={`caro-player-box right ${isP2Turn ? 'active-turn' : ''}`}>
            <div className="caro-player-avatar">{room.player2?.avatar || (isBotMode ? '🤖' : '👤')}</div>
            <div className="caro-player-meta">
              <strong className="caro-player-name">
                {room.player2 ? getPlayerFullName(room.player2) : 'AI Robot'}
              </strong>
            </div>
            <span className="caro-symbol o">◯</span>
          </div>
        </div>

        <div className="caro-info-row">
          <div className="caro-score-pill">
            <span className="score-num x">{scoreP1}</span>
            <span className="score-divider">-</span>
            <span className="score-num o">{scoreP2}</span>
          </div>

          {room.status === 'playing' ? (
            <div className={`caro-turn-badge ${isMyTurn ? (timeLeft <= 5 ? 'timeout' : 'mine') : 'theirs'}`}>
              <span className="timer-icon">
                {timeLeft <= 0 ? '⚠️ Hết giờ!' : `⏱️ ${timeLeft}s`}
              </span>
              <span className="turn-text">
                {isMyTurn ? 'Lượt của bạn' : 'Đối thủ đi'}
              </span>
            </div>
          ) : (
            <span className="caro-finished-tag">Hết Ván</span>
          )}
        </div>
      </div>

      {room.status === 'playing' && (
        <div className="caro-in-game-actions">
          <button className="caro-action-btn surrender" onClick={handleSurrender}>
            🏳️ Chịu thua
          </button>
          <button className="caro-action-btn leave" onClick={handleInGameLeave}>
            🚪 Rời phòng
          </button>
        </div>
      )}

      {room.status === 'finished' && (
        <div className="caro-rematch-banner">
          <div className="caro-result-title">
            {room.winner === 'DRAW'
              ? '🤝 Ván Đấu Hòa Điểm!'
              : room.winner === currentUser?.id
              ? '🎉 Chúc Mừng! Bạn Đã Chiến Thắng!'
              : '😢 Rất Tiếc! Bạn Đã Thua Ván Này!'}
          </div>

          {room.endReason && (
            <div className="caro-result-reason">{room.endReason}</div>
          )}

          <div className="caro-rematch-actions">
            <button
              className={`caro-btn-rematch ${myRematchStatus ? 'active' : ''}`}
              onClick={handleRematchClick}
            >
              {myRematchStatus
                ? '⏳ Chờ đối thủ đồng ý...'
                : opponentRematchStatus
                ? '🤝 Đối thủ muốn đấu lại! Bấm để đấu ngay'
                : '🔄 Bấm Đấu Lại'}
            </button>
            <button className="caro-btn-leave" onClick={onLeave}>
              🚪 Rời Bàn
            </button>
          </div>
        </div>
      )}

      <CaroBoard
        board={board}
        onCellClick={handleCellClick}
        disabled={!isMyTurn || room.status !== 'playing' || isMoveInFlight}
        winningLine={winningLine}
        lastMove={lastMove}
        gameEnded={room.status === 'finished'}
      />
    </div>
  );
};

export default CaroGame;
